import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { CrewDataTableFrame, CrewDataTablePrompt, CrewReportViewAction } from './reportShared';
import { DateInput, Select } from '../../components/ui';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import type { CrewVesselListResponse, CrewVesselListRow } from './types';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type AppliedFilters = { vesselId: string; periodFrom: string; periodTo: string };

const LIST_ROUTE = '/crew/list';
const STORAGE_KEY = getListStateStorageKey(LIST_ROUTE);

function buildListPath(applied: AppliedFilters): string {
  const qs = new URLSearchParams({
    vesselId: applied.vesselId,
    periodFrom: applied.periodFrom,
    periodTo: applied.periodTo,
  });
  return `${LIST_ROUTE}?${qs.toString()}`;
}

function loadPersistedFilters(): AppliedFilters | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<AppliedFilters>;
    if (!p.vesselId || !p.periodFrom || !p.periodTo) return null;
    return {
      vesselId: String(p.vesselId),
      periodFrom: String(p.periodFrom),
      periodTo: String(p.periodTo),
    };
  } catch {
    return null;
  }
}

function persistFilters(applied: AppliedFilters | null): void {
  try {
    if (!applied) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(applied));
  } catch {
    // quota / private mode
  }
}

function filtersFromSearchParams(params: URLSearchParams): AppliedFilters | null {
  const vesselId = params.get('vesselId')?.trim() ?? '';
  const periodFrom = params.get('periodFrom')?.trim() ?? '';
  const periodTo = params.get('periodTo')?.trim() ?? '';
  if (!vesselId || !periodFrom || !periodTo) return null;
  return { vesselId, periodFrom, periodTo };
}

function formatWage(row: CrewVesselListRow): string {
  if (row.wage == null) return '-';
  const amount = typeof row.wage === 'number' ? row.wage : Number(row.wage);
  if (!Number.isFinite(amount)) return String(row.wage);
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return row.wageCurrency ? `${formatted} ${row.wageCurrency}` : formatted;
}

const TextCell: React.FC<{ value: string | null | undefined; className?: string }> = ({
  value,
  className,
}) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm whitespace-nowrap', colors.text, className)}>{value || ''}</span>;
};

const LinkCell: React.FC<{ to: string; from?: string; children: React.ReactNode }> = ({
  to,
  from,
  children,
}) => {
  const { colors } = useTheme();
  return (
    <Link
      to={to}
      state={from ? { from } : undefined}
      className={cn('text-sm hover:underline whitespace-nowrap', colors.primaryFg)}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </Link>
  );
};

const CrewListPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialApplied = useMemo(() => {
    return filtersFromSearchParams(searchParams) ?? loadPersistedFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- hydrate once on mount

  const [vesselId, setVesselId] = useState<string>(() => initialApplied?.vesselId ?? '');
  const [periodFrom, setPeriodFrom] = useState(() => initialApplied?.periodFrom ?? todayIso());
  const [periodTo, setPeriodTo] = useState(() => initialApplied?.periodTo ?? todayIso());
  const [applied, setApplied] = useState<AppliedFilters | null>(() => initialApplied);

  const listReturnPath = applied ? buildListPath(applied) : LIST_ROUTE;

  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(
    () =>
      (lookups?.vessels ?? []).map((v) => ({
        value: String(v.id),
        label: v.vesselName,
      })),
    [lookups?.vessels],
  );

  const listUrl = applied
    ? `/crew/vessel-list?vesselId=${encodeURIComponent(applied.vesselId)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<CrewVesselListResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    persistFilters(applied);
    if (!applied) {
      if (searchParams.toString()) setSearchParams({}, { replace: true });
      return;
    }
    const next = new URLSearchParams({
      vesselId: applied.vesselId,
      periodFrom: applied.periodFrom,
      periodTo: applied.periodTo,
    });
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [applied, searchParams, setSearchParams]);

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setApplied({ vesselId, periodFrom, periodTo });
  }, [vesselId, periodFrom, periodTo]);

  const openCrew = useCallback(
    (crewId: number) => {
      navigate(`/crew/${crewId}`, { state: { from: listReturnPath } });
    },
    [listReturnPath, navigate],
  );

  const rows = data?.rows ?? [];
  const summary = data?.summary;
  const vessel = data?.vessel;

  const columns: Column<CrewVesselListRow>[] = useMemo(
    () => [
      {
        key: 'crewId',
        header: 'Id',
        sortable: true,
        render: (v) => <TextCell value={v != null ? String(v) : ''} />,
      },
      {
        key: 'lastName',
        header: 'Lastname',
        sortable: true,
        render: (_v, row) => (
          <LinkCell to={`/crew/${row.crewId}`} from={listReturnPath}>
            {row.lastName}
          </LinkCell>
        ),
      },
      {
        key: 'firstName',
        header: 'Firstname',
        sortable: true,
        render: (_v, row) => (
          <LinkCell to={`/crew/${row.crewId}`} from={listReturnPath}>
            {row.firstName}
          </LinkCell>
        ),
      },
      {
        key: 'nationality',
        header: 'Nationality',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'age',
        header: 'Age',
        sortable: true,
        footerType: 'avg',
        footerRender: (avg) =>
          typeof avg === 'number' ? avg.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '-',
        render: (v) => <TextCell value={v != null ? String(v) : ''} />,
      },
      {
        key: 'lastRank',
        header: 'Last Rank',
        sortable: true,
        render: (_v, row) =>
          row.lastRank ? (
            <LinkCell to={`/crew/${row.crewId}/file`} from={listReturnPath}>
              {row.lastRank}
            </LinkCell>
          ) : (
            <TextCell value="" />
          ),
      },
      {
        key: 'wage',
        header: 'Wage',
        sortable: true,
        footerType: 'sum',
        footerRender: (sum) =>
          typeof sum === 'number'
            ? sum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '-',
        render: (_v, row) => <TextCell value={formatWage(row) === '-' ? '' : formatWage(row)} />,
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: (v) => (
          <TextCell value={v} className={v === 'ONB' ? 'font-semibold text-emerald-700' : undefined} />
        ),
      },
      {
        key: 'departure',
        header: 'Departure',
        sortable: false,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'signOnDate',
        header: 'S/On Date',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDateGlobal(v) : ''} />,
      },
      {
        key: 'monthsOnboard',
        header: 'M.ONB',
        sortable: true,
        footerType: 'sum',
        footerRender: (sum) => (typeof sum === 'number' ? String(Math.round(sum)) : '-'),
        render: (v) => <TextCell value={v != null ? String(v) : ''} />,
      },
      {
        key: 'intentionSignOffDate',
        header: 'Intention S/Off',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDateGlobal(v) : ''} />,
      },
      {
        key: 'signOffDate',
        header: 'S/Off Date',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDateGlobal(v) : ''} />,
      },
      {
        key: 'signOffPort',
        header: 'S/Off Port',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'signOffReason',
        header: 'S/Off Reason',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'repatriation',
        header: 'Repatriation',
        sortable: false,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'earliest',
        header: 'Earliest',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDateGlobal(v) : ''} />,
      },
      {
        key: 'coeDate',
        header: 'COE',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDateGlobal(v) : ''} />,
      },
      {
        key: 'latest',
        header: 'Latest',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDateGlobal(v) : ''} />,
      },
      {
        key: 'extCoeDate',
        header: 'Ext.COE',
        sortable: true,
        render: (v) => <TextCell value={v ? formatDateGlobal(v) : ''} />,
      },
    ],
    [listReturnPath],
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hideBack
        hint={{
          title: 'Crew List',
          body: 'Shows crew assigned to a vessel during the selected period, including rank, wage, sign-on/off dates, COE, and months onboard. Choose a vessel and date range, then search.',
        }}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading}
        onRefresh={applied ? () => mutate() : undefined}
        context={
          vessel && applied
            ? `${vessel.vesselName}${vessel.vesselAbrv ? ` (${vessel.vesselAbrv})` : ''} · ${applied.periodFrom} → ${applied.periodTo}`
            : undefined
        }
      >
        <CrewReportFilterField label="Vessel" htmlFor="crew-list-vessel" width="fill">
          <Select
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            options={vesselOptions}
            placeholder="Select vessel…"
            searchPlaceholder="Search vessels…"
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period from" htmlFor="crew-list-from" width="md">
          <DateInput
            id="crew-list-from"
            name="periodFrom"
            value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value)}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period to" htmlFor="crew-list-to" width="md">
          <DateInput
            id="crew-list-to"
            name="periodTo"
            value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value)}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {!applied ? (
          <CrewDataTablePrompt>Select a vessel and period, then search.</CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt>
            <span className={isDark ? 'text-red-400' : 'text-red-600'}>Could not load crew list.</span>
          </CrewDataTablePrompt>
        ) : (
          <DataTable<CrewVesselListRow>
            data={rows}
            columns={columns}
            keyExtractor={(row) => row.transactionId}
            loading={isLoading}
            emptyMessage="No crew assignments for this vessel and period."
            showFooter
            footerData={rows}
            onRowClick={(row) => openCrew(row.crewId)}
            actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
            actionsPosition="left"
          />
        )}
      </CrewDataTableFrame>

      {applied && summary && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-6 rounded-xl border px-4 py-3 text-sm shadow-sm',
            colors.surface,
            colors.border,
          )}
        >
          <div>
            <span className={colors.textSecondary}>No of Crew</span>
            <span className={cn('ml-2 font-semibold', colors.text)}>{summary.crewCount}</span>
          </div>
          <div>
            <span className={colors.textSecondary}>Safe Equip</span>
            <span className={cn('ml-2 font-medium', colors.text)}>
              {vessel?.safeEquip ? 'Yes' : 'No'}
            </span>
          </div>
          <div>
            <span className={colors.textSecondary}>Safe Manning</span>
            <span className={cn('ml-2 font-medium', colors.text)}>
              {vessel?.safeManning != null ? vessel.safeManning : '—'}
            </span>
          </div>
          {summary.avgAge != null && (
            <div>
              <span className={colors.textSecondary}>Avg age</span>
              <span className={cn('ml-2 font-medium', colors.text)}>{summary.avgAge}</span>
            </div>
          )}
          <div>
            <span className={colors.textSecondary}>Total wage</span>
            <span className={cn('ml-2 font-medium', colors.text)}>
              {summary.totalWage.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div>
            <span className={colors.textSecondary}>Total M.ONB</span>
            <span className={cn('ml-2 font-medium', colors.text)}>{summary.totalMonthsOnboard}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrewListPage;
